import { useModal } from "@/Contexts";
import Magnifier from "@/Icons/Magnifier";
import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { Button } from "@/components/button";
import ModalContainer from "@/components/modalContainer";
import { Translate, useTrans } from "@/translations";

import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { cloneDeep, set } from "lodash";
import { IdentityKey } from "server/coreApi/identities/types.js";
import { NormalizedCustomerIdentities } from "server/coreApi/identities/types.js";
import { twMerge } from "tailwind-merge";

import { TableWithAutocomplete } from "./TableWithAutocomplete";
import {
  getAutocompleteRow,
  getDefaultRow,
  getTableColumns,
  getTableRows,
} from "./functions";
import {
  AccessTokenColumn,
  AccessTokenRow,
  FieldType,
  IdentityStatus,
} from "./types";

type Props = {
  customerId: string;
  customerName: string;
  refetch?: () => Promise<any>;
};

export const AccessTokensModal: FC<Props> = ({ customerId, refetch }) => {
  const { t } = useTrans();
  const { hideModal } = useModal();
  const [isRefetching, setIsRefetching] = useState(false);
  const { setGlobalAlert } = useGlobalAlert();
  const [search, setSearch] = useState("");
  const [existingRows, setExistingRows] = useState<AccessTokenRow[]>([]);
  const [newRows, setNewRows] = useState<AccessTokenRow[]>([]);

  const trpcUtils = trpc.useUtils();

  const {
    data: allIdentitiesData,
    isLoading: isLoadingIdentities,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = trpc.identities.get.useInfiniteQuery(
    { customerIds: [customerId], limit: 100 },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      gcTime: 0,
    },
  );

  const identities = useMemo(
    () =>
      allIdentitiesData?.pages.flatMap(
        (page) => page.items,
      ) as NormalizedCustomerIdentities,
    [allIdentitiesData],
  );

  useEffect(() => {
    if (identities) {
      const tableRows = getTableRows(identities);
      setExistingRows(tableRows);
    }
  }, [identities]);

  const filteredIdentities = useMemo(
    () =>
      [...newRows, ...existingRows]?.filter((row: any) =>
        search
          ? [row.iso.value, row.em.value, row.printed.value].some(
              (value: any) => {
                return value
                  ?.toString()
                  .toLowerCase()
                  .includes(search?.toLowerCase());
              },
            )
          : true,
      ),
    [newRows, existingRows, search],
  );

  useEffect(() => {
    if (hasNextPage && !!search && !filteredIdentities.length) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, filteredIdentities, search]);

  const { mutateAsync: addIdentitiesMutation, isPending: isAddingIdentities } =
    trpc.identities.addToCustomer.useMutation({
      trpc: {
        context: {
          skipBatch: true, // Update needs to be done after add
        },
      },
    });
  const { mutateAsync: updateStatusMutation, isPending: isUpdatingStatues } =
    trpc.identities.updateStatuses.useMutation({
      trpc: {
        context: {
          skipBatch: true, // Delete needs to be done after update
        },
      },
    });
  const {
    mutateAsync: deleteIdentitiesMutation,
    isPending: isDeletingIdentites,
  } = trpc.identities.delete.useMutation();

  const isLoading =
    isLoadingIdentities ||
    isUpdatingStatues ||
    isDeletingIdentites ||
    isAddingIdentities ||
    isRefetching;

  const onSubmit = useCallback(async () => {
    try {
      // add found identities

      const identitiesToAdd = newRows?.filter((row) => row.identityId);
      if (identitiesToAdd?.length) {
        await addIdentitiesMutation({
          customerId,
          identityIds: identitiesToAdd.map((el) => el.identityId!),
        });
      }
      const identitiesToEdit = existingRows?.filter((at) =>
        identities?.find(
          (identity) =>
            identity.iso === at.iso.value &&
            identity.status !== at.status.value,
        ),
      );

      // Done after create since we need to update the status of the new identities
      if (identitiesToAdd?.length || identitiesToEdit?.length) {
        await updateStatusMutation({
          customerId,
          statusUpdates: [...identitiesToAdd, ...identitiesToEdit].map(
            (identity) => ({
              id: identity.iso.value,
              externalKeyName: "ISO",
              status: identity.status.value ?? IdentityStatus.ACTIVE,
            }),
          ),
        });
      }
      // Delete identities
      const identitiesToDelete = identities?.filter(
        (identity) => !existingRows?.find((i) => i.iso.value === identity.iso),
      );
      if (identitiesToDelete?.length) {
        await deleteIdentitiesMutation({
          customerId,
          ids: identitiesToDelete.map((identity) => identity.id!),
        });
      }

      setIsRefetching(true);
      await refetch?.();
      setIsRefetching(false);

      setGlobalAlert({
        type: "success",
        message: "systemMessages:accessTokensUpdated",
      });
      hideModal();
    } catch (error: Error | any) {
      console.error(error);
      setGlobalAlert({
        type: "error",
        message: "systemMessages:accessTokensUpdateFailed",
        instructions: error?.message,
      });
    }
  }, [
    newRows,
    existingRows,
    addIdentitiesMutation,
    customerId,
    deleteIdentitiesMutation,
    hideModal,
    refetch,
    setGlobalAlert,
    updateStatusMutation,
    identities,
  ]);

  const columns: AccessTokenColumn[] = useMemo(() => getTableColumns(t), [t]);

  const getAutocompleteOptions = useCallback(
    async (key: IdentityKey, value: string) => {
      if (value) {
        const { options, existsAssigned } =
          await trpcUtils.client.identities.getAutocompleteOptionsWithValidation.query(
            {
              key,
              value,
            },
          );

        return {
          options,
          hasError: existsAssigned,
        };
      } else {
        return {
          options: [],
          hasError: false,
        };
      }
    },
    [trpcUtils],
  );

  const onAutocompleteOptionSelected = useCallback(
    (rowId: string, option: any) => {
      const updatedRows = newRows.map((row) => {
        if (row.id === rowId) {
          return getAutocompleteRow(rowId, option);
        } else {
          return row;
        }
      });

      setNewRows(updatedRows);
    },
    [newRows, setNewRows],
  );

  // Called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        // Once the user has scrolled within 300px of the bottom of the table, fetch more data if there is any
        if (
          scrollHeight - scrollTop - clientHeight < 750 &&
          !isFetchingNextPage &&
          hasNextPage
        ) {
          fetchNextPage();
        }
      }
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage],
  );

  const addRow = useCallback(() => {
    const newRow = getDefaultRow();

    const updatedRows = [newRow, ...newRows];

    setNewRows(updatedRows);
  }, [newRows, setNewRows]);

  const editCell = useCallback(
    (
      rowId: string,
      isNewRow: boolean,
      columnKey: FieldType,
      value: string,
      error: string = "",
    ) => {
      const updateData = (prevData: AccessTokenRow[]) => {
        const updatedData = cloneDeep(prevData);
        const rowIndex = updatedData.findIndex((el) => el.id === rowId);

        let errorMessage = error;
        if (!error) {
          const column = columns.find((col) => col.name === columnKey);
          if (column?.unique) {
            updatedData.forEach((row, rowIndex) => {
              if (
                row[columnKey].value &&
                row[columnKey].value === value &&
                row.id !== rowId
              ) {
                errorMessage = "errors:mustBeUnique";
                set(
                  updatedData,
                  `[${rowIndex}][${columnKey}].error`,
                  errorMessage,
                );
              }
            });
          }

          if (column?.required && !value) {
            errorMessage = "errors:required";
          }
        }

        const newCellValue = {
          value,
          error: errorMessage,
        };

        set(updatedData, `[${rowIndex}][${columnKey}]`, newCellValue);
        return updatedData;
      };

      if (isNewRow) {
        setNewRows((rows) => updateData(rows));
      } else {
        setExistingRows((rows) => updateData(rows));
      }
    },
    [setNewRows, setExistingRows, columns],
  );

  const setCellError = useCallback(
    (isNewRow: boolean, rowId: string, columnKey: FieldType, error: string) => {
      const updateData = (prevData: AccessTokenRow[]) => {
        const updatedData = cloneDeep(prevData);
        const rowIndex = updatedData.findIndex((el) => el.id === rowId);

        set(updatedData, `[${rowIndex}][${columnKey}].error`, error);

        return updatedData;
      };

      if (isNewRow) {
        setNewRows((rows) => updateData(rows));
      } else {
        setExistingRows((rows) => updateData(rows));
      }
    },
    [setNewRows, setExistingRows],
  );

  const removeRow = useCallback(
    (row: AccessTokenRow) => {
      if (row.isNew) {
        const updatedRows = newRows.filter((r) => r.id !== row.id);

        columns
          .filter((col) => col.unique)
          .forEach((col) => {
            updatedRows.forEach((row, rowIndex) => {
              const hasDuplicates = updatedRows
                .filter((r) => r.id !== row.id)
                .some((r) => r[col.name].value === row[col.name].value);

              if (row[col.name].value && hasDuplicates) {
                set(
                  updatedRows,
                  `[${rowIndex}][${col.name}].error`,
                  "errors:mustBeUnique",
                );
              } else {
                const hadDuplicateError =
                  row[col.name].error === "errors:mustBeUnique";
                set(
                  updatedRows,
                  `[${rowIndex}][${col.name}].error`,
                  hadDuplicateError ? "" : row[col.name].error,
                );
              }
            });
          });

        setNewRows(updatedRows);
      } else {
        const updatedRows = existingRows.filter((r) => r.id !== row.id);
        setExistingRows(updatedRows);
      }
    },
    [newRows, setNewRows, existingRows, setExistingRows, columns],
  );

  const isDirty = useMemo(
    () =>
      !!newRows.length ||
      existingRows.length !== identities?.length ||
      existingRows.some((row) => {
        const prevRow = identities?.find((identity) => identity.id === row.id);

        return row.status.value !== prevRow?.status;
      }),
    [newRows, existingRows, identities],
  );
  const isValid = useMemo(
    () =>
      ![...newRows, ...existingRows].some((row) =>
        ["iso", "em", "printed"].some(
          (key: string) => !!row[key as FieldType].error,
        ),
      ),
    [newRows, existingRows],
  );

  return (
    <ModalContainer
      title={"actions:manageAccess"}
      onCancel={hideModal}
      onConfirm={() => onSubmit()}
      onConfirmLoading={isLoading}
      showDiscardModal={isDirty}
      onConfirmDisabled={!isValid || !isDirty || isLoading}
      onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
    >
      <div className="mb-5 max-w-[1000px] min-w-[800px]">
        <div className="bg-grey1 mb-10 p-5">
          <h2 className="font-dm-sans mb-2 text-base font-medium">
            {t("hints:manageAccessKeysTitle")}
          </h2>
          <Translate
            i18nKey={"hints:manageAccessKeysDescription"}
            components={[<p className="text-sm" />]}
          />
        </div>
        {!isError && isLoadingIdentities && (
          <div className="flex justify-center">
            <LoadingIndicator />
          </div>
        )}
        {isError && (
          <p className="text-sm font-bold text-red-500">
            {t("systemMessages:loadingAccessTokensFailed")}
          </p>
        )}
        {!isError && !isLoadingIdentities && (
          <>
            <div className="relative mb-6 w-full">
              <p className="mb-1 text-sm font-bold">
                {t("hints:searchForAccessTokens")}
              </p>
              <Magnifier
                className="text-grey6 absolute mt-2.5 ml-2"
                width={12}
                height={12}
              />
              <input
                onChange={(e) => setSearch(e.target.value)}
                type="text"
                placeholder={t("genericLabels:searchbarPlaceholder")}
                className={twMerge(
                  "font-dm-sans placeholder:text-grey6 disabled:border-grey3 disabled:text-grey6 box-border h-8 w-64 rounded-none border border-black py-[7.5px] pr-3 pl-6 text-sm",
                  "focus:outline-2 focus:-outline-offset-2 focus:outline-solid",
                )}
                data-1p-ignore
              />

              <div className="float-right">
                <Button
                  label="actions:addRow"
                  onClick={addRow}
                  type="button"
                  color="secondary"
                />
              </div>
            </div>
            <TableWithAutocomplete
              columns={columns}
              emptyMessage={t("systemMessages:noAccessTokenAdded")}
              data={filteredIdentities}
              getAutocompleteOptions={getAutocompleteOptions}
              onAutocompleteOptionSelected={onAutocompleteOptionSelected}
              editCell={editCell}
              setCellError={setCellError}
              removeRow={removeRow}
            />
          </>
        )}
      </div>
    </ModalContainer>
  );
};
